/**
 * Bounded retry + request timeout for the API-Gateway-fronted REST reads.
 *
 * HITL-1800. Every REST call out of this app was single-shot and unbounded: no
 * timeout, no retry. One 503 or 429 from API Gateway took an agent out of
 * service, and a connection stalled at the edge sat behind the loading spinner
 * for the browser default (~300s) with no failure and no fallback. The two
 * websockets (`app/middleware.ts`, `useTaskRouterWS`) and the issue-report
 * transport already had their own backoff; this is the same idea for the
 * request paths that didn't.
 *
 * Deliberately NOT a global fetch/axios interceptor. A blanket retry would also
 * replay order submissions and event posts, and an automatic duplicate final
 * order is its own incident class (HITL-1513). Callers opt in, one path at a
 * time — today the menu and menu-auth reads in `utils/network.ts` and the auth
 * calls in `userSlice`.
 */
import logger from './logger';

/** Attempts in total, not retries after the first — 3 means at most 2 resends. */
export const DEFAULT_ATTEMPTS = 3;
/**
 * The loosest bound we put on anything: API Gateway's own integration timeout
 * is 30s on HTTP APIs (29s on REST), so past here the gateway has given up on
 * the backend too. It is not a guarantee that nothing is still coming — the
 * client's clock starts when the request is sent, the gateway's only once it
 * calls the backend, and today's incident is precisely the gap between them
 * (5-25s of front-door latency). So this bounds a hang without being
 * aggressive, but it is still a bound: any path where an abort is worse than a
 * wait — order submission above all — must opt out with `timeoutMs: 0`.
 */
export const GATEWAY_TIMEOUT_MS = 30000;
/**
 * Per-attempt for the retried reads, where waiting out the full gateway timeout
 * is worse than failing early and letting the retry (or the menu cache) take
 * over. Worst case is roughly 3 * 12s + backoff ≈ 40s.
 */
export const DEFAULT_TIMEOUT_MS = 12000;

export const BASE_BACKOFF_MS = 300;
export const MAX_BACKOFF_MS = 4000;
/** A throttle may ask for minutes; an agent waiting on a menu cannot give them. */
const MAX_RETRY_AFTER_MS = 5000;

// Same escape hatch the issue-report transport and the New Relic batcher use:
// no real waiting under Jest, so retry paths stay unit-testable without fake
// timers and no background delay outlives a test run.
const isTestEnv = process.env.NODE_ENV === 'test';

export interface RetryOptions {
  /** Total attempts including the first. */
  attempts?: number;
  /** Per-attempt timeout; 0 disables the abort. */
  timeoutMs?: number;
  /** Shows up in the retry warning so a log line names its own caller. */
  label?: string;
}

/**
 * Transient only. 4xx other than these means the request itself is wrong and
 * resending it unchanged just burns the agent's time — 401/403 in particular
 * must reach the caller, which is what tells auth a token is genuinely dead
 * rather than a casualty of the outage.
 */
export const isRetryableStatus = (status: number): boolean =>
  status === 408 || status === 425 || status === 429 || status >= 500;

/**
 * The only two statuses that mean the credential itself was rejected. Anything
 * else — a 429, a 5xx, a timeout, a network error — is the transport failing,
 * and must never be allowed to end an agent's session. The single rule for
 * every path that can call `logout`.
 *
 * This is about a token the *server* rejected. A token that was never there at
 * all is `MissingAuthTokenError` below; both end the session, for different
 * reasons.
 */
export const isAuthRejectionStatus = (status?: number): boolean =>
  status === 401 || status === 403;

/**
 * What the menu reads throw once a request has failed, carrying enough for the
 * caller to tell a rejected token from a bad minute on the network — the
 * distinction `isAuthRejectionStatus` exists for, and the one a bare thrown
 * `error_code` could not express.
 *
 * `toJSON` is here because these callers log with `JSON.stringify(error)`,
 * which renders a plain Error as `{}`.
 */
export class HttpRequestError extends Error {
  readonly status: number;

  readonly errorCode?: unknown;

  readonly errorMessage?: unknown;

  constructor(status: number, errorCode?: unknown, errorMessage?: unknown) {
    super(`HTTP ${status}${errorCode ? ` (${String(errorCode)})` : ''}`);
    this.name = 'HttpRequestError';
    this.status = status;
    this.errorCode = errorCode;
    this.errorMessage = errorMessage;
    // Required for `instanceof` to survive TypeScript's ES5 class downlevel.
    Object.setPrototypeOf(this, HttpRequestError.prototype);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      errorCode: this.errorCode,
      errorMessage: this.errorMessage,
    };
  }
}

/**
 * Thrown where the app has no agent session token to send at all — cleared
 * storage, or a sign-out in another tab.
 *
 * It is not a server response, so it has no status, but it ends the session for
 * the same reason a 401 does: there is no credential to retry with, and no
 * amount of waiting produces one. Before HITL-1800 the menu reads threw a bare
 * string here and their callers logged out on any error, this case included;
 * once those callers started gating on the status, a plain string stopped
 * matching and an agent with no token sat "signed in" with an empty menu until
 * the next 30-minute `refreshAuthToken`. Typed so the gate can let it through.
 *
 * Only for the *agent's* credential. A missing service token — the configured
 * `MU_MENU_API_TOKEN` on the brand-menu path — must not use this: signing the
 * agent out does not fix a misconfigured deployment.
 */
export class MissingAuthTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingAuthTokenError';
    // Required for `instanceof` to survive TypeScript's ES5 class downlevel.
    Object.setPrototypeOf(this, MissingAuthTokenError.prototype);
  }

  // Same reason as HttpRequestError.toJSON: these callers log with
  // `JSON.stringify(error)`, which renders a plain Error as `{}`.
  toJSON() {
    return { name: this.name, message: this.message };
  }
}

/**
 * The single gate for every path that can call `logout`: true only when the
 * agent's credential is the problem — rejected by the server (401/403), or
 * absent entirely. Never true for a transport failure.
 */
export const isAuthRejectionError = (error: unknown): boolean =>
  error instanceof MissingAuthTokenError ||
  (error instanceof HttpRequestError && isAuthRejectionStatus(error.status));

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, isTestEnv ? 0 : ms));

/** `Retry-After` is either delta-seconds or an HTTP date; both are capped. */
export const parseRetryAfterMs = (header?: string | null): number | null => {
  if (!header) return null;

  const seconds = Number(header);
  if (Number.isFinite(seconds)) {
    return Math.min(Math.max(seconds, 0) * 1000, MAX_RETRY_AFTER_MS);
  }

  const parsed = Date.parse(header);
  if (Number.isNaN(parsed)) return null;
  return Math.min(Math.max(parsed - Date.now(), 0), MAX_RETRY_AFTER_MS);
};

/**
 * Exponential with full jitter in [1x, 2x), capped — the same shape as the
 * websocket reconnect backoff, so a fleet coming back from an outage doesn't
 * resend in lockstep and re-throttle the gateway it is waiting on.
 *
 * The base and cap are parameters so the websocket reconnect in
 * `app/middleware.ts` can share this instead of recomputing it inline. Its
 * waits today are exactly `backoffMs(retries, { baseMs: 2000, maxMs:
 * WEBSOCKET_RETRY_MAX_BACKOFF })`; switching it over is a follow-up, because it
 * also touches the HITL-1743 drop banner and its retry counting.
 */
export const backoffMs = (
  attempt: number,
  { baseMs = BASE_BACKOFF_MS, maxMs = MAX_BACKOFF_MS } = {}
): number => {
  const exponential = baseMs * 2 ** (attempt - 1);
  return Math.min(exponential * (1 + Math.random()), maxMs);
};

const withTimeout = async (
  url: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> => {
  // No caller passes its own signal today; if one ever does, this would clobber
  // it and the two need composing instead.
  if (!timeoutMs) return fetch(url, init);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

/**
 * `fetch` with a per-attempt timeout and a bounded retry on transient failures.
 *
 * Returns the Response — including a non-OK one once the attempts are spent, so
 * the caller still owns what a failed status means for its own path. Throws
 * only what `fetch` throws (network error, or the timeout's AbortError) after
 * the last attempt.
 */
export const fetchWithRetry = async (
  url: string,
  init: RequestInit = {},
  options: RetryOptions = {}
): Promise<Response> => {
  const {
    attempts = DEFAULT_ATTEMPTS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    label = 'request',
  } = options;

  for (let attempt = 1; ; attempt += 1) {
    const isLastAttempt = attempt >= attempts;

    try {
      const response = await withTimeout(url, init, timeoutMs);
      if (response.ok || isLastAttempt || !isRetryableStatus(response.status)) {
        return response;
      }

      logger.warn({
        message: `Retrying ${label} after HTTP ${response.status}`,
        moreInfo: { url, attempt, attempts },
      });
      // `headers` is guarded because a stubbed Response in tests may not have it.
      const retryAfter = parseRetryAfterMs(
        response.headers?.get?.('Retry-After')
      );
      await sleep(retryAfter ?? backoffMs(attempt));
    } catch (error) {
      if (isLastAttempt) throw error;

      logger.warn({
        message: `Retrying ${label} after network error`,
        moreInfo: { url, attempt, attempts, error: String(error) },
      });
      await sleep(backoffMs(attempt));
    }
  }
};
